import React, { useState, useEffect, useMemo, useContext } from "react";
import { hot } from "react-hot-loader/root";
import ReactMapGL, { FlyToInterpolator } from "react-map-gl";
import { InView } from "react-intersection-observer";
import styled from "@emotion/styled";
import bbox from "@turf/bbox";

import "mapbox-gl/dist/mapbox-gl.css";

import { is768Query } from "~/core/scripts/mediaquery-helper";
import { TranslationContext } from "~/kits/general/scripts/app.js";

import DistrictsLayer from "./DistrictsLayer";
import mapStyle from "./style.json";
import {
  InnerWrapper,
  StoryItems,
  StoryItemWrapper,
} from "../Explainer/Explainer.styles";

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 1px 0;
  margin-bottom: calc(100vh + 32px); /* hack */
`;

const MapWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  width: 100%;
  pointer-events: none;
  height: 0; /* hack */
  .mapboxgl-ctrl-attrib {
    opacity: 0;
  }
`;

const RestyledStoryItemWrapper = styled(StoryItemWrapper)`
  background: none;
  text-shadow: 1px 1px 1px rgba(255, 255, 255, 0.5),
    -1px 1px 1px rgba(255, 255, 255, 0.5), 1px -1px 1px rgba(255, 255, 255, 0.5),
    -1px -1px 1px rgba(255, 255, 255, 0.5);

  margin-bottom: 70vh;

  &:first-of-type {
    margin-top: 35vh;
  }

  > div {
    position: relative;
    max-width: 540px;
    ${(p) => p.theme.breakpoints.sm.down} {
      font-size: 16px;
    }

    &:after {
      position: absolute;
      content: "";
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: ${({ theme }) => theme.colors.grey90};
      filter: blur(10px);
      z-index: -1;
    }
  }
`;

const StoryItem = ({ onEnterViewport, isActive, children }) => {
  const onInViewChange = (inView, entry) => {
    if (inView) {
      onEnterViewport();
    }
  };

  const isDesktop = is768Query();

  return (
    <InView
      as={RestyledStoryItemWrapper}
      threshold={isDesktop ? 0.8 : 0.4}
      onChange={onInViewChange}
      isActive={isActive}
    >
      {children}
    </InView>
  );
};

const initialViewport = {
  width: "100%",
  height: "100vh",
};

const Map = ({ data }) => {
  const polyglot = useContext(TranslationContext);

  const items = [
    {
      zoomFeature: { state: "24", id: "03" },
      content: (
        <div dangerouslySetInnerHTML={{ __html: polyglot.t("mapText1") }} />
      ),
    },
    {
      zoomFeature: { state: "48", id: "35" },
      content: (
        <div dangerouslySetInnerHTML={{ __html: polyglot.t("mapText2") }} />
      ),
    },
    {
      zoomFeature: { state: "39", id: "03" },
      content: (
        <div dangerouslySetInnerHTML={{ __html: polyglot.t("mapText3") }} />
      ),
    },
  ];

  const [activeItem, setActiveItem] = useState(items[0]);
  const [viewport, setViewport] = useState(initialViewport);
  const [map, setMap] = useState(null);
  const [isInitial, setInitial] = useState(true);

  const onViewportChange = (nextVp) => {
    setViewport((vp) => ({
      ...vp,
      ...nextVp,
    }));
  };

  const zoomFeature = useMemo(
    () =>
      data.features.find(
        (feat) =>
          feat.properties.state === activeItem.zoomFeature.state &&
          feat.properties.id === activeItem.zoomFeature.id
      ),
    [activeItem, data]
  );

  useEffect(() => {
    if (map) {
      if (zoomFeature) {
        const box = bbox(zoomFeature);

        const padding =
          window.innerWidth > 768
            ? {
                top: 50,
                right: window.innerWidth / 2,
                bottom: 50,
                left: 50,
              }
            : {
                top: 10,
                right: 10,
                bottom: 10,
                left: 10,
              };

        const camera = map.cameraForBounds(box, {
          padding,
        });

        const transitionProps = isInitial
          ? {}
          : {
              transitionInterpolator: new FlyToInterpolator({ speed: 2 }),
              transitionDuration: "auto",
            };

        onViewportChange({
          latitude: camera.center.lat,
          longitude: camera.center.lng,
          zoom: camera.zoom,
          ...transitionProps,
        });

        setInitial(false);
      }
    }
  }, [activeItem, map, zoomFeature]);

  return (
    <Wrapper>
      <InnerWrapper style={{ maxWidth: "unset" }}>
        <MapWrapper style={{ opacity: isInitial ? 0 : 1 }}>
          <ReactMapGL
            mapStyle={mapStyle}
            mapboxApiAccessToken="pk.eyJ1IjoiYmVybGluZXJtb3JnZW5wb3N0IiwiYSI6Ik93Q3pCbWsifQ.-e0ddIaG2WuJdsA-TLeX5A"
            onViewportChange={onViewportChange}
            {...viewport}
            scrollZoom={false}
            dragPan={false}
            dragRotate={false}
            doubleClickZoom={false}
            touchZoom={false}
            touchRotate={false}
            onLoad={(evt) => setMap(evt.target)}
          >
            <DistrictsLayer
              data={data}
              zoomFeatureFilter={activeItem.zoomFeature}
            />
          </ReactMapGL>
        </MapWrapper>
        <StoryItems style={{ zIndex: 100 }}>
          {items.map((item, i) => (
            <StoryItem
              key={`story-item-${i}`}
              onEnterViewport={() => setActiveItem(items[i])}
              isActive={activeItem === i}
            >
              {item.content}
            </StoryItem>
          ))}
        </StoryItems>
      </InnerWrapper>
    </Wrapper>
  );
};

export default hot(Map);
